<script setup>
import { computed, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  companyId: {
    type: Number,
    required: true,
  },
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const companiesStore = useCompaniesStore();
const { t } = useI18n();

const pending = ref(false);
const errorMessage = ref('');
const retryOperation = ref(null);
const persist = async operation => {
  if (pending.value) return;
  pending.value = true;
  errorMessage.value = '';
  retryOperation.value = operation;
  try {
    if (operation.type === 'delete') {
      await companiesStore.deleteCustomAttributes({
        id: props.companyId,
        customAttributes: [props.attribute.attributeKey],
      });
      useAlert(t('COMPANIES.DETAIL.ATTRIBUTES.MESSAGES.DELETE_SUCCESS'));
    } else {
      await companiesStore.update({
        id: props.companyId,
        customAttributes: { [props.attribute.attributeKey]: operation.value },
      });
      useAlert(t('COMPANIES.DETAIL.ATTRIBUTES.MESSAGES.UPDATE_SUCCESS'));
    }
    retryOperation.value = null;
  } catch {
    errorMessage.value = t('WORKSPACE_V2.COMPANY_DETAIL.ATTRIBUTE_ERROR');
  } finally {
    pending.value = false;
  }
};
const handleDelete = () => persist({ type: 'delete' });
const handleUpdate = value => persist({ type: 'update', value });

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(
  () =>
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
);
</script>

<template>
  <fieldset
    :disabled="pending"
    class="m-0 border-0 p-0 grid grid-cols-[140px,1fr] group/attribute items-center w-full gap-2"
    :class="isEditingView ? 'min-h-10' : 'min-h-11'"
  >
    <div class="flex items-center justify-between truncate">
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ attribute.attributeDisplayName }}
      </span>
    </div>

    <component
      :is="CurrentAttributeComponent"
      :attribute="attribute"
      :is-editing-view="isEditingView"
      @update="handleUpdate"
      @delete="handleDelete"
    />
    <div
      v-if="errorMessage"
      role="alert"
      class="col-span-2 text-xs text-n-ruby-11"
    >
      <p>{{ errorMessage }}</p>
      <Button
        :label="t('WORKSPACE_V2.COMPANY_DETAIL.RETRY')"
        :disabled="pending"
        @click="persist(retryOperation)"
      />
    </div>
  </fieldset>
</template>
